/**
 * The menus' controls (spec 08 §4), one `MenuInput` a frame: the arrows, WASD and player 1's own
 * keys to move, Enter or player 1's fire to choose, Esc or Backspace to go back; pad 0's d-pad or
 * stick, A or X to choose, B back; player 2's fire (keys or pad 1) for the title's two-player start;
 * the mouse. A held direction repeats, as menus do.
 */

import type { MenuInput } from '../ui/menu';
import type { KeyBindings, Keyboard } from './keyboard';

/** Frames a direction is held before it repeats, and then every how many frames (at 50 Hz). */
const REPEAT_DELAY = 16;
const REPEAT_EVERY = 4;
const STICK = 0.5;

type Dir = 'up' | 'down' | 'left' | 'right';
const DIRS: readonly Dir[] = ['up', 'down', 'left', 'right'];
const DIR_KEYS: Record<Dir, readonly string[]> = {
  up: ['ArrowUp', 'KeyW'], down: ['ArrowDown', 'KeyS'], left: ['ArrowLeft', 'KeyA'], right: ['ArrowRight', 'KeyD'],
};

interface Rect { left: number; top: number; width: number; height: number }

function pad(index: number): Gamepad | null {
  return navigator.getGamepads?.()[index] ?? null;
}

const pressed = (p: Gamepad | null, i: number): boolean => p?.buttons[i]?.pressed === true;

export class MenuControls {
  private readonly heldFrames: Record<Dir, number> = { up: 0, down: 0, left: 0, right: 0 };
  private padPrev = new Set<string>();
  private mouse: { x: number; y: number; click: boolean } | null = null;

  /** `rect`: where the 640x360 HUD sits on the page now (the image's rect), for the mouse. */
  constructor(target: Window, private readonly rect: () => Rect) {
    const at = (e: MouseEvent): { x: number; y: number } => {
      const r = this.rect();
      return { x: ((e.clientX - r.left) / Math.max(1, r.width)) * 640, y: ((e.clientY - r.top) / Math.max(1, r.height)) * 360 };
    };
    target.addEventListener('mousemove', (e) => {
      this.mouse = { ...at(e), click: this.mouse?.click ?? false };
    });
    target.addEventListener('mousedown', (e) => {
      if (e.button === 0) this.mouse = { ...at(e), click: true };
    });
  }

  private edges = new Set<string>();

  /** Once per 50 Hz frame, first: read the pads' buttons, so `startPressed` and `read` see this frame's presses. */
  poll(): void {
    const now = new Set<string>();
    for (const i of [0, 1]) {
      pad(i)?.buttons.forEach((b, n) => { if (b.pressed) now.add(`${i}:${n}`); });
    }
    this.edges = new Set([...now].filter((k) => !this.padPrev.has(k)));
    this.padPrev = now;
  }

  /** Was a pad's START pressed this frame (the pause toggle, like P)? */
  startPressed(): boolean {
    return this.edges.has('0:9') || this.edges.has('1:9');
  }

  /** This frame's menu input: after `poll`, before the keyboard's `endFrame`. */
  read(keys: Keyboard, bindings: readonly [KeyBindings, KeyBindings]): MenuInput {
    const edges = this.edges;
    const p0 = pad(0);
    const x = p0?.axes[0] ?? 0;
    const y = p0?.axes[1] ?? 0;
    const padDir: Record<Dir, boolean> = {
      up: pressed(p0, 12) || y < -STICK, down: pressed(p0, 13) || y > STICK,
      left: pressed(p0, 14) || x < -STICK, right: pressed(p0, 15) || x > STICK,
    };
    const own: Record<Dir, readonly string[]> = {
      up: DIR_KEYS.up, down: DIR_KEYS.down, left: [...DIR_KEYS.left, ...bindings[0].left], right: [...DIR_KEYS.right, ...bindings[0].right],
    };
    const dir = {} as Record<Dir, boolean>;
    for (const d of DIRS) {
      const down = keys.isDown(own[d]) || padDir[d];
      this.heldFrames[d] = down ? this.heldFrames[d] + 1 : 0;
      const n = this.heldFrames[d];
      const repeat = n > REPEAT_DELAY && (n - REPEAT_DELAY) % REPEAT_EVERY === 0;
      dir[d] = keys.anyPressed(own[d]) || (n === 1 && padDir[d]) || repeat;
    }
    const mouse = this.mouse;
    this.mouse = null;
    return {
      ...dir,
      select: keys.anyPressed(['Enter', 'NumpadEnter', ...bindings[0].fire]) || edges.has('0:0') || edges.has('0:2') || edges.has('0:9'),
      back: keys.anyPressed(['Escape', 'Backspace']) || edges.has('0:1'),
      p2Fire: keys.anyPressed(bindings[1].fire) || edges.has('1:0') || edges.has('1:1') || edges.has('1:2'),
      key: keys.firstPressed(),
      pointer: mouse,
    };
  }
}
